import React from 'react';
import styled, {keyframes} from 'styled-components';
import {RentalProvider, useRentUserAndWebsiteConfigurationContext} from './Context';
import BikeList from './components/BikeList';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Contact from './components/Contact';
import Services from './components/Services';
import {Helmet} from 'react-helmet';
import {BrowserRouter as Router, Route, Routes} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import Home from './components/Home';

const AppContainer = styled.div`
    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

const Content = styled.div`
    flex: 1;
    min-height: 100vh;
`;

const SpinnerContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    background-color: ${props => props.theme.colors.background}; // #E8ECEF
`;

const spin = keyframes`
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
`;

const Spinner = styled.div`
    width: 50px;
    height: 50px;
    border: 5px solid ${props => props.theme.colors.secondary}50;
    border-top: 5px solid ${props => props.theme.colors.primary};
    border-radius: 50%;
    animation: ${spin} 1s linear infinite;
`;

function MainApp() {

    const {rentUser, config, loading, error} = useRentUserAndWebsiteConfigurationContext();
    const {t} = useTranslation();

    if (loading) {
        return (
            <SpinnerContainer>
                <Spinner/>
            </SpinnerContainer>
        );
    }

    if (error) {
        return (
            <SpinnerContainer>
                <p>{t('errorLoading')}</p>
            </SpinnerContainer>
        );
    }

    const pageTitle = rentUser?.name || t('welcome');
    const pageDescription = config?.heroText || t('heroText');
    const heroImageUrl = config?.heroImages?.[0]?.url;

    return (
        <AppContainer>
            <Navbar rentUser={rentUser} config={config}/>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription}/>
                <meta name="keywords" content="Bike rental, cycling, LoceM, noleggio bici"/>
                <meta property="og:site_name" content={pageTitle}/>
                <meta property="og:title" content={pageTitle}/>
                <meta property="og:description" content={pageDescription}/>
                <meta property="og:type" content="website"/>
                {heroImageUrl && <meta property="og:image" content={heroImageUrl}/>}
                <meta name="twitter:card" content="summary_large_image"/>
                <meta name="twitter:title" content={pageTitle}/>
                <meta name="twitter:description" content={pageDescription}/>
                {heroImageUrl && <meta name="twitter:image" content={heroImageUrl}/>}
            </Helmet>
            <Content>
                <Routes>
                    <Route path="/"
                           element={<Home rentUser={rentUser} config={config} loading={loading} error={error}/>}/>
                    <Route path="/bikes" element={<BikeList rentUser={rentUser}/>}/>
                    <Route path="/contact" element={<Contact rentUser={rentUser}/>}/>
                    {config?.isServicesPageAvailable && (
                        <Route path="/services" element={<Services rentUser={rentUser} config={config}/>}/>
                    )}
                </Routes>
            </Content>
            <Footer rentUser={rentUser} config={config}/>
        </AppContainer>
    );
}

function App() {
    return (
        <Router>
            <RentalProvider>
                <MainApp/>
            </RentalProvider>
        </Router>
    );
}

export default App;
